import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Loader2, RotateCw, User } from "lucide-react";
import { v2AdminPreview, v2AdminGrantInternal } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

export default function PreviewAsLearner() {
  const { courseSlug } = useParams();
  const { user } = useAuth();
  const [days, setDays] = useState(0);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [granted, setGranted] = useState(null);

  const load = async () => {
    setBusy(true); setErr("");
    try { setPreview(await v2AdminPreview(courseSlug, days)); }
    catch (e) { setErr(e?.response?.data?.detail || "Vorschau fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseSlug]);

  const grantMe = async () => {
    setBusy(true);
    try {
      const r = await v2AdminGrantInternal(courseSlug, user.id);
      setGranted(r);
    } catch (e) { setErr(e?.response?.data?.detail || "Grant fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div className="pt-24 min-h-screen bg-[#060810]" data-testid="preview-as-learner-page">
      {/* Preview Banner */}
      <div className="sticky top-0 z-40 bg-[#D4AF37] text-[#081328] py-3 px-6 flex items-center gap-3 text-sm font-semibold" data-testid="preview-banner">
        <Eye size={14} />
        PREVIEW-MODUS · Nur Simulation. Keine echten Entitlements, keine Writes, keine E-Mails.
      </div>

      <div className="container-sat py-8 max-w-5xl">
        <Link to="/admin/v2/courses" className="btn-ghost text-sm"><ArrowLeft size={14} /> Kurse</Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mt-6">
          <div>
            <div className="overline">Vorschau · als Teilnehmer</div>
            <h1 className="serif text-3xl md:text-4xl font-light">{preview?.course?.title || courseSlug}</h1>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-white/60">
              <div className="overline mb-1">Tage nach Enrollment</div>
              <input type="number" min="0" value={days} onChange={(e) => setDays(parseInt(e.target.value || 0))}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm w-32" data-testid="preview-days-input" />
            </label>
            <button onClick={load} disabled={busy} className="btn-primary text-sm" data-testid="preview-refresh-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><RotateCw size={14} /> Simulieren</>}
            </button>
            <button onClick={grantMe} disabled={busy} className="btn-secondary text-sm" data-testid="preview-grant-btn" title="INTERNAL_TEST-Zugang für den eigenen Account (nur intern)">
              <User size={14} /> INTERNAL_TEST-Zugang aktivieren
            </button>
          </div>
        </div>

        {granted && (
          <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 mt-4 text-sm text-white/80" data-testid="grant-confirmation">
            Zugang aktiviert. Öffne den Kurs unter <Link to={`/academy/v2/${courseSlug}`} className="underline" data-testid="grant-course-link">/academy/v2/{courseSlug}</Link>.
          </div>
        )}
        {err && <div className="text-red-300 text-sm mt-4">{err}</div>}

        {preview && (
          <div className="mt-6 grid gap-3" data-testid="preview-lesson-grid">
            {preview.lessons.map((l) => (
              <div key={l.id}
                data-testid={`preview-lesson-${l.position}`}
                className={`rounded-2xl p-5 flex items-center gap-4 border ${l.unlocked ? "border-[#D4AF37]/30 bg-white/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${l.unlocked ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-white/15 text-white/40"}`}>
                  {l.position}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline">{l.lesson_type} · {l.status}</div>
                  <div className="serif text-lg mt-0.5">
                    {l.unlocked ? l.title : <span className="text-white/40">Noch nicht freigegeben</span>}
                  </div>
                  {!l.unlocked && (
                    <div className="text-xs text-white/50 mt-1">
                      {l.lock_reason}
                      {l.unlock_at && ` · ${new Date(l.unlock_at).toLocaleString("de-CH")}`}
                    </div>
                  )}
                </div>
                <div className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border ${l.unlocked ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-white/15 text-white/40"}`}>
                  {l.unlocked ? "FREI" : "SPERRE"}
                </div>
                <div className="text-white/40 text-xs shrink-0 hidden md:block max-w-[220px] text-right">
                  Admin: {l._preview_admin_title || "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
